import React, { useEffect, useState } from 'react'
import { injectIntl } from 'react-intl'
import Spinner from '../../util/spinner-shim'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import moment from 'moment'
import fileD from 'js-file-download'
import { useParams, useNavigate } from 'react-router-dom'

import Highcharts from '../../highchartsInit'
import { Chart as HighchartsReact } from '@highcharts/react'

import statusCalculate from 'altrac-application-status'
import {
  isNumber, litersToUserPreference, splitTemplate,
} from 'altrac-conversions'
import { IconLink } from '../partials/Navigation/Buttons'
import AppDispatcher from '../../dispatchers/AppDispatcher'
import AppStore from '../../stores/AppStore'
import withAppStore from '../../util/withAppStore'
import NavigationBar from '../partials/NavigationBar'
import Footer from '../partials/Footer'
import Headline from '../partials/ConfigurePageComponents/Headline'
import TelemetryButtons from '../partials/ConfigurePageComponents/TelemetryButtons'
import DateRangePicker from '../partials/ConfigurePageComponents/DateRangePicker'
import { useDateRangePicker } from '../partials/ConfigurePageComponents/useDateRangePicker'
import pageStyles from '../../../css/devicePage.module.css'


const split = (input, physical) => {
  if (!input) {
    return null
  }
  const output = input.match(/\{\{([a-zA-Z0-9 ]*\|[a-zA-Z0-9 ]*)\}\}/)
  if (!output) {
    return input
  }
  const arr = output[1].split('|')
  let returnValue
  if (physical && typeof physical === 'object' && physical.hasOwnProperty(arr[0])) {
    returnValue = physical[arr[0]]
  } else {
    returnValue = arr[1]
  }
  if (isNumber(returnValue)) returnValue = Number(returnValue)
  return returnValue
}

const Sections = (props) => {
  const {
    dateBegin,
    dateEnd,
    dateType,
    dayPickerIsVisible,
    draftRange,
    downloadCSV,
    chart,
    unitsOfFlow,
    unitsOfVolume,
    device,
    deviceStatus,
    customer,
    statsLookup,
    statsReady,
    pinCode,
    section: {
      name: sectionName,
      chart: chartInterface,
      telemetryTypes,
    },
    flowStats,
    flowReady,
    editDateRange,
    closeDayPicker,
    handleDayClick,
    index,
    chartsData,
    level,
    tempConv,
    retryGetStats,
  } = props

  let physical
  if (device && device.physical) physical = device.physical
  let volumeTitle = 'Acre Feet'
  switch (unitsOfVolume) {
    case 'liters':
      volumeTitle = 'Liters'
      break
    case 'gallons':
      volumeTitle = 'Gallons'
      break
    case 'acreInch':
      volumeTitle = 'Acre Inch'
      break
    case 'acreFeet':
      volumeTitle = 'Acre Feet'
      break
    case 'cubicFeet':
      volumeTitle = 'Cubic Feet'
      break
    case 'hectareM':
      volumeTitle = 'Hectare Meter'
      break
    case 'cubicM':
      volumeTitle = 'Cubic Meter'
      break
    default:
      volumeTitle = 'Acre Feet'
  }

  const [chartInst, setChartInst] = useState(false)

  const catchChart = (_chartInst) => {
    setChartInst(_chartInst)
  }

  const divStyle = { float: 'left', width: '100%' }
  if (index > 0) divStyle.marginTop = '20px'
  const sectionNameD = split(sectionName, physical)
  const toReturn = [
    <div key={0} style={divStyle} className={pageStyles.sectionName}>
      <h4>{sectionNameD}</h4>
    </div>,
  ]

  const height = '225px'
  const buttonStyle = {
    cursor: 'pointer',
    '&:focus': { outline: '#CCC' },
    '&:hover': { background: '#CCC' },
    paddingRight: '15px',
  }

  const placeholderContainerStyle = {
    padding: '10px 0px',
    height: `calc(${height} + 57px)`,
    float: 'left',
    position: 'relative',
  }

  const placeholderInnerStyle = {
    height: '100%',
    width: '100%',
    float: 'left',
    position: 'relative',
    backgroundColor: 'white',
  }

  const placeholderErrorStyle = {
    ...placeholderInnerStyle,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '0 24px',
  }

  const retryButtonStyle = {
    marginTop: '12px',
    padding: '8px 16px',
    backgroundColor: '#284047',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  }

  let seriesData
  const dailySummary = flowStats.daily_summary
  const {
    name = '',
    series = [],
    yAxis = [{
      min: 0,
      max: 1,
      title: {
        enabled: false,
      },
      allowDecimals: false,
      endOnTick: false,
      startOnTick: false,
      opposite: true,
      visible: false,
    }, {
      title: {
        enabled: false,
      },
      allowDecimals: false,
    }],
  } = chartInterface

  if (chartInterface.type === 'flow') {
    const dailyFlow = []
    const acreFeet = []

    if (flowReady === 'loaded') {
      const sData = flowStats.series
      const sortedSData = sData.slice().sort((a, b) => new Date(a.date) - new Date(b.date))

      sortedSData.map((item) => {
        if (item) {
          dailyFlow.push([moment(item.date).valueOf(), litersToUserPreference(item.ratePerMin, unitsOfFlow, 'flow', 0)])
        }
      })

      dailySummary.map((item) => {
        const flowCumulative = item.flow_cumulative
        acreFeet.push([moment(item.date_begin).valueOf(), litersToUserPreference(flowCumulative, unitsOfVolume, 'volume', 2)])
      })
      if (dailyFlow.length === sData.length) {
        const dataReady = chartsData && (statsReady === 'loaded')

        seriesData = series.reduce((acc, cur) => {
          if (!dataReady || !chartsData[name] || !physical) return acc
          let {
            label,
          } = cur
          label = splitTemplate(label, physical, true)
          if (chartsData[name][label]) {
            const {
              type,
              color,
              shadow = false,
              yAxis: yAxisSer = 0,
              pointWidth,
              lineWidth = 2,
              fillOpacity = 1,
            } = cur
            acc.push({
              name: label,
              data: chartsData[name][label],
              type,
              color,
              fillOpacity,
              shadow,
              lineWidth,
              yAxis: yAxisSer,
              pointWidth,
            })
          }
          return acc
        }, [])

        // TEST:  Only show Acre Feet and Flow Rate on flow chart; ignore configured series
        seriesData = []

        seriesData.push(
          {
            turboThreshold: 6000,
            name: `${volumeTitle}`,
            animation: false,
            data: acreFeet,
            type: 'column',
            yAxis: 0,
            pointPadding: 0.1,
            groupPadding: 0,
            pointRange: 24 * 3600 * 1000,
          },
          {
            turboThreshold: 6000,
            name: `Flow Rate (${unitsOfFlow})`,
            animation: false,
            data: dailyFlow,
            type: 'line',
            yAxis: 1,
            pointRange: 24 * 3600 * 1000,
            cropThreshold: 500,
          }
        )

        toReturn.push(
          <div key={1} style={{ float: 'left' }}>
            <div className={pageStyles.chartContainer} style={{ padding: '10px 0px', height: 'inherit' }}>
              <div className={pageStyles.dateRangePickerInside} style={{ display: 'flex' }}>
                <DateRangePicker
                  dateBegin={dateBegin}
                  dateEnd={dateEnd}
                  dateType={dateType}
                  dayPickerIsVisible={dayPickerIsVisible}
                  draftRange={draftRange}
                  onClose={closeDayPicker}
                  onEditRange={editDateRange}
                  onDayClick={handleDayClick}
                  layout='inline'
                  buttonStyle={{ flex: 1, marginLeft: '35px' }}
                />
                <button
                  type='button'
                  onClick={() => downloadCSV(chartInst)}
                  className={pageStyles.chartTitleSetting}
                  style={buttonStyle}
                >
                  <i className='fa fa-download' />
                  {' '}
                </button>
              </div>
              <HighchartsReact
                highcharts={Highcharts}
                ref={chart}
                callback={catchChart}
                 options={{
                   accessibility: {
                     enabled: false,
                   },
                   legend: {
                     reversed: true,
                   },
                   time: {
                    useUTC: false,
                  },
                  title: {
                    text: '',
                  },
                  chart: {
                    zoomType: 'x',
                    panning: true,
                    animation: false,
                    resetZoomButton: {
                      theme: {
                        display: 'none',
                      },
                    },
                  },
                  tooltip: {
                    xDateFormat: '%b %e %H:%M',
                    shared: true,
                  },
                  xAxis: {
                    type: 'datetime',
                    dateTimeLabelFormats: {
                      day: '%e %b',
                      month: '%b %e',
                      year: '%b',
                    },
                    title: {
                      enabled: false,
                    },
                    tickInterval: 1000 * 60 * 60 * 24,
                    minRange: 3600000,
                  },
                  plotOptions: {
                    column: {
                      pointPlacement: 0.5,
                    },
                    series: {
                      states: {
                        inactive: {
                          opacity: 1,
                        },
                      },
                    },
                  },
                  yAxis: [
                    {
                      labels: {
                        format: '{value}',
                        style: {
                          color: '#6AAEF8',
                        },
                      },
                      title: {
                        text: `${volumeTitle}`,
                        style: {
                          color: '#6AAEF8',
                        },
                      },
                      opposite: true,
                      min: 0,
                    },
                    {
                      gridLineWidth: 0,
                      labels: {
                        format: '{value}',
                      },
                      title: {
                        text: `Flow Rate (${unitsOfFlow})`,
                        style: {
                          color: '#284047',
                        },
                      },
                      min: 0,
                      minRange: 1000,
                    }],
                  series: seriesData,
                  colors: ['#6AAEF8', '#284047'],
                  exporting: {
                    enabled: false,
                  },
                  credits: {
                    enabled: false,
                  },
                }}
              />
            </div>
          </div>
        )
      }
    } else {
      toReturn.push(
        <div
          key='placeholder'
          className={pageStyles.chartContainer}
          style={placeholderContainerStyle}
        >
          {flowReady === 'error'
            ? (
              <div style={placeholderErrorStyle}>
                <span style={{ fontWeight: 600, color: '#284047' }}>Unable to load flow data.</span>
                <span style={{ marginTop: '4px', color: '#5a6b70' }}>Please try again in a moment.</span>
                {typeof retryGetStats === 'function'
                  ? (
                    <button
                      type='button'
                      onClick={retryGetStats}
                      style={retryButtonStyle}
                    >
                      Try again
                    </button>
                    )
                  : null}
              </div>
              )
            : (
              <div style={placeholderInnerStyle}>
                <Spinner
                  name='wave'
                  color='#284047'
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '50%',
                    transform: 'translate(50%, -50%)',
                  }}
                />
              </div>
              )}
        </div>
      )
    }
  }

  if (telemetryTypes && Array.isArray(telemetryTypes) && telemetryTypes.length > 0) {
    toReturn.push(
      <div key={3} style={{ float: 'left' }}>
        <TelemetryButtons
          device={device}
          status={deviceStatus}
          flowStats={flowStats}
          dailySummary={dailySummary}
          flowReady={flowReady}
          path={`/${device.customer_id}/devices/${device.id}/`}
          telemetryTypes={telemetryTypes}
          statsLookup={statsLookup}
          statsReady={statsReady}
          customer={customer}
          pinCode={pinCode}
          volumeTitle={volumeTitle}
          level={level}
          tempConv={tempConv}
        />
      </div>
    )
  } else if (seriesData && seriesData.length > 0) {
    const outputs = []
    seriesData.forEach((iter) => {
      outputs.push(
        <IconLink
          key={`${iter.name}${iter.color}`}
          label={iter.name}
          value={iter.data && iter.data.length > 0 && iter.data[iter.data.length - 1][1] ? iter.data[iter.data.length - 1][1] : 0}
        />
      )
    })
    toReturn.push(
      <div key={4} className={pageStyles.tileContainer} style={{ float: 'left' }}>
        {outputs}
      </div>
    )
  }
  return toReturn
}

function FlowPage (props) {
  const { customerID, deviceID } = useParams()
  const navigate = useNavigate()
  const chart = React.useRef()

  // Use the shared date range picker hook
  const dateRangePicker = useDateRangePicker(
    moment().startOf('day').subtract(10, 'days'),
    moment().endOf('day')
  )

  const [state, setState] = React.useState({
    viewDays: props.viewDays || 10,
    statsReady: 'notLoaded',
  })

  // Sync date range picker state with local state when needed
  const { dateBegin, dateEnd, dateType, dayPickerIsVisible, draftRange } = dateRangePicker.state

  const resizeTimeout = React.useRef(false)
  const once = React.useRef(false)

  const getDevice = React.useCallback(() => {
    AppDispatcher.dispatch({ action: 'get-device', deviceID })
  }, [deviceID])

  const getStats = React.useCallback(() => {
    const { data: { lookupReady, lookup } = {} } = AppStore
    const device = lookup[deviceID]

    if (lookupReady && device) {
      const { address } = device

      AppDispatcher.dispatch({
        action: 'get-stats-flow-address',
        address,
        timeBeg: moment(dateBegin).format('YYYY-MM-DD'),
        timeEnd: moment(dateEnd).format('YYYY-MM-DD'),
      })
      AppDispatcher.dispatch({
        action: 'get-valve-stats',
        deviceID,
        timeBeg: dateBegin.toISOString(),
        timeEnd: dateEnd.toISOString(),
        utcOffset: moment().utcOffset() / 60,
      })
    }
  }, [deviceID, dateBegin, dateEnd])

  const unloadFlowStats = React.useCallback(() => {
    AppDispatcher.dispatch({
      action: 'unload-flow-stats',
    })
  }, [])

  const back = React.useCallback(() => {
    const { data: { history: { list = [] } = {} } = {} } = props
    navigate(list.length > 0 ? list : '/home')
    unloadFlowStats()
  }, [props.data.history.list, navigate, unloadFlowStats])

  const goto = React.useCallback((path) => {
    navigate(path)
  }, [navigate])

  const toAdvancedSettings = React.useCallback(() => {
    goto(`/${customerID}/devices/${deviceID}/settings`)
  }, [customerID, deviceID, goto])

  // Use handlers from the date range picker hook
  const { closeDayPicker, editDateRange, handleDayClick, resetToLast31Days, applyDraftRange } = dateRangePicker

  const resetDateRange = React.useCallback(() => {
    const dateBegin = moment()
      .startOf('day')
      .subtract(state.viewDays, 'days')
      .add(1, 'days')

    const dateEnd = moment()
      .endOf('day')

    dateRangePicker.setDateRange(dateBegin, dateEnd)
  }, [state.viewDays, dateRangePicker])

  // Call getStats when date range changes
  React.useEffect(() => {
    getStats()
  }, [dateBegin, dateEnd, getStats])

  const onChange = React.useCallback(() => {
    const { statsReady } = state
    const {
      data: {
        statsReady: statsReadyN,
      },
    } = AppStore
    if (statsReady !== statsReadyN) {
      setState(prev => ({ ...prev, statsReady: statsReadyN }))
    }
  }, [state.statsReady])

  const handleResize = React.useCallback(() => {
    clearTimeout(resizeTimeout.current)
    resizeTimeout.current = setTimeout(() => {
      // Force re-render
      setState(prev => ({ ...prev }))
    }, 500)
  }, [])

  const downloadCSV = React.useCallback((chartInstance) => {
    const chartToUse = chartInstance || (chart.current && chart.current.chart)
    if (!chartToUse || typeof chartToUse.getCSV !== 'function') return

    const csv = chartToUse.getCSV().replace(/""/g, '"""')
    const start = moment(dateBegin).format('YYYY-MM-DD')
    const end = moment(dateEnd).format('YYYY-MM-DD')
    const filename = `flow_${deviceID}_${start}_${end}.csv`

    fileD(csv, filename)
  }, [chart, deviceID, dateBegin, dateEnd])

  useEffect(() => {
    getDevice()
    AppStore.addChangeListener(onChange)
    window.addEventListener('resize', handleResize)
    const interval = setInterval(getDevice, 5000)
    const interval2 = setInterval(getStats, 60000)

    return () => {
      AppStore.removeListener('change', onChange)
      window.removeEventListener('resize', handleResize)
      clearInterval(interval)
      clearInterval(interval2)
    }
  }, [unloadFlowStats, getDevice, onChange, handleResize, getStats])

  useEffect(() => {
    const { data: { lookupReady, lookup } = {} } = props || {}
    const device = lookup[deviceID]

    if (lookupReady && device) {
      const {
        physical: {
          flowSettings: {
            viewDays: newViewDays = 10,
          } = {},
        } = {},
      } = device || {}

      const { viewDays } = state

      if (viewDays !== newViewDays) {
        setState(prev => ({ ...prev, viewDays: newViewDays }))
        resetDateRange()
      } else if (!once.current) {
        getStats()
        once.current = true
      }
    }
  }, [props.data.lookupReady, props.data.lookup, deviceID, state.viewDays, resetDateRange, getStats])

  const render = () => {
    const {
      data: {
        lookupReady,
        lookup,
        stats: { charts: chartsData } = {},
        statsLookup,
        pinCode,
        customer: {
          data: customer,
        },
        flowStats,
        flowReady,
      } = {},
    } = AppStore

    const {
      statsReady,
    } = state

    const {
      dateBegin,
      dateEnd,
      dateType,
      dayPickerIsVisible,
    } = dateRangePicker.state

    const {
      auth: { logout },
      route: { path: page },
      intl: { formatMessage },
    } = props

    const {
      locale: {
        unitsOfVolume = 'acreFeet',
        unitsOfFlow = 'gpm',
      } = {},
    } = AppStore.getUser() || {}

    const title = `${formatMessage({
      id: 'flow.stats.title',
      defaultMessage: 'Flow',
      description: 'Flow page title',
    })}`

    let pageContents = [(
      <div style={{ height: '400px', position: 'relative' }}>
        <Spinner
          name='wave'
          style={{
            position: 'absolute',
            top: '50%',
            right: '50%',
            transform: 'translate(50%, -50%)',
          }}
        />
      </div>
    )]

    let name
    let device
    let headline = ''

    let deviceStatus
    if (lookupReady && lookup) {
      device = lookup[deviceID]
      deviceStatus = statusCalculate(device)

      const {
        physical: {
          deviceNumber,
        } = {},
      } = device || {}

      name = deviceNumber

      const {
        deviceHeadlineDanger,
        deviceHeadlineWarning,
      } = deviceStatus

      headline = (
        <div>
          <Headline
            info={deviceHeadlineDanger}
            prepend={formatMessage({
              id: 'headline.error',
              defaultMessage: 'ERROR: ',
              description: '',
            })}
          />
          <Headline
            info={deviceHeadlineWarning}
            prepend={formatMessage({
              id: 'headline.warning',
              defaultMessage: 'WARNING: ',
              description: '',
            })}
          />
        </div>
      )
    }

    if (lookupReady && lookup) {
      const {
        interface_versioned: {
          data: {
            primaryPage: {
              sections = [],
            } = {},
          } = {},
        } = {},
      } = device || {}

      if (sections && Array.isArray(sections) && sections.length > 0) {
        pageContents = sections.map((e, index) => (
          <Sections
            device={device}
            downloadCSV={downloadCSV}
            chart={chart}
            key={`${device.id}-${index}`}
            unitsOfVolume={unitsOfVolume || 'acreFeet'}
            unitsOfFlow={unitsOfFlow || 'gpm'}
            deviceStatus={deviceStatus}
            customer={customer}
            statsLookup={statsLookup}
            statsReady={statsReady}
            pinCode={pinCode}
            dateBegin={dateBegin}
            dateEnd={dateEnd}
            dateType={dateType}
            dayPickerIsVisible={dayPickerIsVisible}
            section={e}
            chartsData={chartsData}
            flowStats={flowStats}
            flowReady={flowReady}
            editDateRange={editDateRange}
            draftRange={draftRange}
            closeDayPicker={closeDayPicker}
            handleDayClick={handleDayClick}
            index={index}
            level={props.level}
            tempConv={props.data.user && props.data.user.locale.tempConv || 'f'}
            retryGetStats={getStats}
          />
        ))
      }
    }

    return (
      <div>
        <Helmet><title>{title}</title></Helmet>
        <DateRangePicker
          dateBegin={dateBegin}
          dateEnd={dateEnd}
          dateType={dateType}
          dayPickerIsVisible={dayPickerIsVisible}
          draftRange={draftRange}
          onClose={closeDayPicker}
          onEditRange={editDateRange}
          onDayClick={handleDayClick}
          onApplyRange={applyDraftRange}
          onResetToLast31Days={resetToLast31Days}
          layout='modal'
        />
        <NavigationBar
          backFunc={back}
          page='/flow'
          logOut={() => props.auth.logout(navigate)}
          name={name}
          rightControl={{
            icon: 'fa-cog',
            func: toAdvancedSettings,
          }}
        />
        <div className={pageStyles.pageWidth}>
          <div className={pageStyles.pageContainer}>
            {headline}
            {pageContents}
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return render()
}

FlowPage.propTypes = {
  auth: PropTypes.object,
  data: PropTypes.object,
  viewDays: PropTypes.number,
}

export default withAppStore(injectIntl(FlowPage))
